"use client";

import { useCallback, useSyncExternalStore } from "react";

/**
 * External store tracking which node is currently being hovered as a valid
 * drop target while the user drags a connection. Used to render the same
 * pulsating halo as the auto-run scheduler so the user gets a clear visual
 * cue that releasing the connection here will link to that node.
 *
 * Kept off the React Flow node data (so it never pollutes the saved canvas /
 * undo history) and off React Context (so an unrelated node becoming/ceasing
 * to be the target doesn't re-render every node). Only one node can be the
 * active drop target at a time.
 */

let currentId: string | null = null;
const idListeners = new Map<string, Set<() => void>>();

const notifyId = (id: string) => {
  const set = idListeners.get(id);
  if (!set) return;
  for (const fn of set) {
    try {
      fn();
    } catch {}
  }
};

export function setConnectDropTarget(id: string | null): void {
  const next = id ?? null;
  if (next === currentId) return;
  const prev = currentId;
  currentId = next;
  if (prev) notifyId(prev);
  if (next) notifyId(next);
}

export function clearConnectDropTarget(): void {
  setConnectDropTarget(null);
}

function subscribe(id: string, cb: () => void): () => void {
  let set = idListeners.get(id);
  if (!set) {
    set = new Set<() => void>();
    idListeners.set(id, set);
  }
  set.add(cb);
  return () => {
    const s = idListeners.get(id);
    if (!s) return;
    s.delete(cb);
    if (s.size === 0) idListeners.delete(id);
  };
}

const getServerSnapshot = () => false;

/**
 * React hook — returns `true` while `id` is the current connection drop
 * target. Subscribed per id, so an unrelated node becoming the target does
 * NOT re-render this node.
 */
export function useIsConnectDropTarget(id: string): boolean {
  const subscribeForId = useCallback(
    (cb: () => void) => subscribe(id, cb),
    [id],
  );
  const getSnapshot = useCallback(() => currentId === id, [id]);
  return useSyncExternalStore(subscribeForId, getSnapshot, getServerSnapshot);
}
